import type { ApiRequest, ApiRequestWith } from "@/api/types";
import type {
  BatchCosignBody,
  BatchCosignResponse,
  BatchRejectBody,
  BatchRejectResponse,
  CosignatureDocumentType,
  CosignatureQueueResponse,
  CosignatureQueueSearchParams,
  CosignMetaBody,
} from "@/core/types";
import { getGroupAuthType } from "@/utils/authToken";
import { GROUP_AUTH_HEADER_NAME } from "@/api/constants";
import { httpAuth2 } from "@/api/auth";

type CosignBody = {
  document_type: CosignatureDocumentType;
  patient_id: string;
};

type RejectBody = {
  document_type: CosignatureDocumentType;
  patient_id: string;
  rejection_reason: string;
};

type CosignatureRequestsTyped = {
  batch_cosign: ApiRequestWith<BatchCosignResponse, { body: BatchCosignBody }>;
  batch_reject: ApiRequestWith<BatchRejectResponse, { body: BatchRejectBody }>;
  cosign_document: ApiRequestWith<void, { body: CosignBody; urlParams: { documentId: string } }>;
  get_cosign_meta: ApiRequestWith<CosignMetaBody, { urlParams: { noteId: string } }>;
  get_cosign_queue: ApiRequest<
    CosignatureQueueResponse,
    { searchParams?: CosignatureQueueSearchParams }
  >;
  get_rejected_notes_count: ApiRequest<{ documents_count: number }>;
  reject_document: ApiRequestWith<void, { body: RejectBody; urlParams: { documentId: string } }>;
};

export const cosignatureRequestsTyped: CosignatureRequestsTyped = {
  batch_cosign: function ({ body, context }) {
    return httpAuth2
      .post("/v1/cosignature/batch_cosign", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
      })
      .json<BatchCosignResponse>();
  },

  batch_reject: function ({ body, context }) {
    return httpAuth2
      .post("/v1/cosignature/batch_reject", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
      })
      .json<BatchRejectResponse>();
  },

  cosign_document: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`/v1/cosignature/${urlParams.documentId}/cosign`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
        // disable timeout for long-polling endpoint — needs more time than the default (10 s)
        timeout: false,
      })
      .json<void>();
  },

  get_cosign_meta: function ({ context, urlParams }) {
    return httpAuth2
      .get(`/v1/cosignature/note/${urlParams.noteId}/meta`, {
        context: { showLoader: false, ...context },
        headers: { [GROUP_AUTH_HEADER_NAME]: getGroupAuthType() },
      })
      .json<CosignMetaBody>();
  },

  get_cosign_queue: function ({ context, searchParams, signal } = {}) {
    return httpAuth2
      .get("/v1/cosignature/queue", {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        searchParams,
        signal,
      })
      .json<CosignatureQueueResponse>();
  },

  get_rejected_notes_count: function ({ context } = {}) {
    return httpAuth2
      .get("/v1/cosignature/rejected_count", {
        context: { showLoader: false, ...context },
      })
      .json<{ documents_count: number }>();
  },

  reject_document: function ({ body, context, urlParams }) {
    return httpAuth2
      .post(`/v1/cosignature/${urlParams.documentId}/reject`, {
        context: { preventAllErrorToast: true, showLoader: false, ...context },
        json: body,
        // disable timeout for long-polling endpoint — needs more time than the default (10 s)
        timeout: false,
      })
      .json<void>();
  },
};
